import filesApi from '../api/files'

// Dodaj stałe z danymi zapasowymi - zgodne z faktyczną strukturą katalogów
const FALLBACK_DIRECTORIES = {
  'models/man': ['John'],  // Model name
  'models/woman': ['Sarah', 'Emily'],
  'models/man/John': ['front', 'pose'],  // Pose directories
  'models/man/John/front': ['shirt', 't-shirt']  // Garment directories
};

/**
 * Helper function to encode file paths, especially handling spaces
 * @param path Path to encode
 * @returns Encoded path suitable for API requests
 */
export const encodeFilePath = (path: string): string => {
  // Split the path by slashes to handle each segment separately
  const segments = path.split('/');
  
  // Encode each segment individually
  const encodedSegments = segments.map(segment => {
    // Replace spaces with %20 but leave other URL-safe characters
    return segment.replace(/\s+/g, '%20');
  });
  
  // Join back with slashes
  return encodedSegments.join('/');
};

/**
 * Class to handle loading of model directories and files
 */
export class AssetLoader {
  /**
   * Load directory structure for the given path
   * @param path Base path to load directories from
   * @returns Promise with an array of directory objects
   */
  static async loadDirectories(path: string): Promise<string[]> {
    try {
      console.log(`🔷 AssetLoader.loadDirectories called with path: ${path}`);
      console.log(`🔷 Calling filesApi.listDirectories`);
      
      const dirs = await filesApi.listDirectories(path);
      
      console.log(`🔷 Received directories:`, dirs);
      console.log(`🔷 Response type:`, typeof dirs);
      console.log(`🔷 Is array?`, Array.isArray(dirs));
      
      // Filtrujemy, aby wykluczyć pliki PNG z listy katalogów
      const filteredDirs = dirs.filter(dir => {
        // Upewnij się, że mamy string
        const dirName = typeof dir === 'string' ? dir : 
                       (typeof dir === 'object' && dir !== null) ? 
                       (Object.prototype.hasOwnProperty.call(dir, 'name') ? String((dir as any).name) : String(dir)) : String(dir);
        
        // Wykluczamy pliki PNG
        return !dirName.toLowerCase().endsWith('.png');
      });
      
      console.log(`🔷 Filtered directories (without PNG files):`, filteredDirs);
      
      return filteredDirs;
    } catch (error) {
      console.error(`Error loading directories for path ${path}:`, error);
      console.error(`Error type:`, typeof error);
      console.error(`Error details:`, JSON.stringify(error, null, 2));
      
      // Użyj danych zapasowych jeśli są dostępne
      if (path in FALLBACK_DIRECTORIES) {
        console.log(`📙 Using fallback data for path: ${path}`);
        return FALLBACK_DIRECTORIES[path as keyof typeof FALLBACK_DIRECTORIES];
      }
      
      return [];
    }
  }

  /**
   * Load images from the given path
   * @param path Path to load images from
   * @param fileExtension Optional file extension to filter (default: all image types)
   * @returns Promise with an array of image URLs
   */
  static async loadImages(path: string, fileExtension: string = ''): Promise<string[]> {
    try {
      // Get files from the API
      const files = await filesApi.listFiles(path)
      
      // Filter to only include image files, optionally by specific extension
      const imageFiles = fileExtension 
        ? files.filter(file => new RegExp(`\\.${fileExtension}$`, 'i').test(file))
        : files.filter(file => /\.(jpg|jpeg|png|gif|webp)$/i.test(file))
      
      // Sort image files alphabetically by name
      imageFiles.sort((a, b) => a.localeCompare(b));
      
      console.log(`🔍 Filtered ${fileExtension || 'image'} files:`, imageFiles);
      
      // Build full URLs for each image, handling spaces in filenames
      return imageFiles.map(file => {
        // Ensure the file path is properly formatted for URLs
        const fullPath = `/${path}/${file}`;
        // We avoid using encodeURI here to prevent double-encoding from the browser
        return fullPath;
      })
    } catch (error) {
      console.error(`Error loading images for path ${path}:`, error)
      return []
    }
  }

  /**
   * Recursively loads directory structure and all subdirectories
   * @param basePath Base path to start from
   * @param depth Optional depth limit for recursion (default: 3)
   * @returns Promise with a hierarchical directory structure
   */
  static async loadDirectoryTree(basePath: string, depth: number = 3): Promise<any> {
    try {
      // Limit recursion depth for performance
      if (depth <= 0) {
        return {
          path: basePath,
          directories: {},
          files: await this.loadImages(basePath, 'png')
        }
      }
      
      const dirs = await this.loadDirectories(basePath)
      
      const result: Record<string, any> = {
        path: basePath,
        directories: {},
        files: await this.loadImages(basePath, 'png')
      }
      
      // Recursively load subdirectories with reduced depth
      for (const dir of dirs) {
        const subPath = `${basePath}/${dir}`
        result.directories[dir] = await this.loadDirectoryTree(subPath, depth - 1)
      }
      
      return result
    } catch (error) {
      console.error(`Error loading directory tree for ${basePath}:`, error)
      return {
        path: basePath,
        directories: {},
        files: []
      }
    }
  }
  
  /**
   * Navigate to a specific directory path and return its contents
   * @param path Full path to navigate to (e.g. 'models/man/shirt/formal')
   * @returns Promise with directories and files at that path
   */
  static async navigateToPath(path: string): Promise<{directories: string[], files: string[]}> {
    try {
      const directories = await this.loadDirectories(path)
      const files = await this.loadImages(path, 'png')
      
      return { directories, files }
    } catch (error) {
      console.error(`Error navigating to path ${path}:`, error)
      return { directories: [], files: [] }
    }
  }
}

// Instead of attaching encodeFilePath directly to AssetLoader class,
// we'll export it separately and use it directly where needed

export default AssetLoader
